import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ParentChildProvider } from "@/contexts/ParentChildContext";
import { Toaster } from "sonner";
import ErrorBoundary from "@/components/ErrorBoundary";
import { lazy, Suspense } from "react";
import { Loader2 } from "lucide-react";

// Yalnızca route dışı bileşenler eager
import Layout from "@/components/Layout";
import RoleGuard from "@/components/RoleGuard";

// Kritik ilk-yükleme rotaları eager
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";

// Tüm diğer sayfalar lazy — kod bölme (P2 performans)
const Homework = lazy(() => import("@/pages/Homework"));
const HomeworkNew = lazy(() => import("@/pages/HomeworkNew"));
const HomeworkDetail = lazy(() => import("@/pages/HomeworkDetail"));
const AITeacher = lazy(() => import("@/pages/AITeacher"));
const AIChecker = lazy(() => import("@/pages/AIChecker"));
const AICoach = lazy(() => import("@/pages/AICoach"));
const AIExamGen = lazy(() => import("@/pages/AIExamGen"));
const Exams = lazy(() => import("@/pages/Exams"));
const ExamDetail = lazy(() => import("@/pages/ExamDetail"));
const Attendance = lazy(() => import("@/pages/Attendance"));
const Messages = lazy(() => import("@/pages/Messages"));
const Gamification = lazy(() => import("@/pages/Gamification"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Users = lazy(() => import("@/pages/Users"));
const Classes = lazy(() => import("@/pages/Classes"));
const Schools = lazy(() => import("@/pages/Schools"));
const Profile = lazy(() => import("@/pages/Profile"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const QRCheckin = lazy(() => import("@/pages/QRCheckin"));
const Certificate = lazy(() => import("@/pages/Certificate"));
const TeacherReport = lazy(() => import("@/pages/TeacherReport"));
const ShareCard = lazy(() => import("@/pages/ShareCard"));
const ParentAlerts = lazy(() => import("@/pages/ParentAlerts"));
const Announcements = lazy(() => import("@/pages/Announcements"));
const CalendarPage = lazy(() => import("@/pages/Calendar"));
const FocusTimer = lazy(() => import("@/pages/FocusTimer"));
const Notebook = lazy(() => import("@/pages/Notebook"));
const RiskInsights = lazy(() => import("@/pages/RiskInsights"));
const QuestionBank = lazy(() => import("@/pages/QuestionBank"));
const Reports = lazy(() => import("@/pages/Reports"));
const TwoFactor = lazy(() => import("@/pages/TwoFactor"));
const AdvancedAnalytics = lazy(() => import("@/pages/AdvancedAnalytics"));
const LiveLessons = lazy(() => import("@/pages/LiveLessons"));
const BulkInvite = lazy(() => import("@/pages/BulkInvite"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const Meetings = lazy(() => import("@/pages/Meetings"));
const TopicGaps = lazy(() => import("@/pages/TopicGaps"));
const Challenge = lazy(() => import("@/pages/Challenge"));
const Forbidden = lazy(() => import("@/pages/Forbidden"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const PlatformLayout = lazy(() => import("@/pages/platform/PlatformLayout"));
const PlatformDashboard = lazy(() => import("@/pages/platform/PlatformDashboard"));
const PlatformTenants = lazy(() => import("@/pages/platform/PlatformTenants"));
const PlatformAudit = lazy(() => import("@/pages/platform/PlatformAudit"));
const PlatformCRM = lazy(() => import("@/pages/platform/PlatformCRM"));
const PlatformFinance = lazy(() => import("@/pages/platform/PlatformFinance"));
const PlatformSupport = lazy(() => import("@/pages/platform/PlatformSupport"));
const PlatformReleases = lazy(() => import("@/pages/platform/PlatformReleases"));
const PlatformAnnouncements = lazy(() => import("@/pages/platform/PlatformAnnouncements"));
const PlatformHealthAlerts = lazy(() => import("@/pages/platform/PlatformHealthAlerts"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

const ADMIN = ["super_admin", "school_admin", "principal", "vice_principal"];
const STAFF = [...ADMIN, "teacher", "counselor"];
const TEACHER_ADMIN = ["teacher", "school_admin", "principal", "vice_principal", "super_admin"];

import "@/App.css";

function PageFallback() {
  return (
    <div className="min-h-[60vh] grid place-items-center" role="status" aria-live="polite" aria-label="Sayfa yükleniyor">
      <Loader2 className="animate-spin text-muted-foreground" size={28} />
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) {
    return (
      <div role="status" aria-live="polite" aria-label="Oturum kontrol ediliyor" className="min-h-[60vh] grid place-items-center">
        <Loader2 className="animate-spin text-muted-foreground" size={26} />
      </div>
    );
  }
  if (!user) {
    const returnTo = encodeURIComponent(loc.pathname + loc.search);
    return <Navigate to={`/giris?returnTo=${returnTo}`} replace />;
  }
  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <ParentChildProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors closeButton />
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/giris" element={<Login />} />
                <Route path="/kayit" element={<Register />} />
                <Route path="/fiyatlandirma" element={<Pricing />} />
                <Route path="/odeme/basarili" element={<PaymentSuccess />} />
                <Route path="/odeme/iptal" element={<PaymentCancel />} />
                <Route path="/yoklama/kod/:token" element={<QRCheckin />} />
                <Route path="/paylas/:token" element={<ShareCard />} />
                <Route path="/rapor/:token" element={<TeacherReport />} />
                <Route path="/davet/:token" element={<AcceptInvite />} />
                <Route path="/sifremi-unuttum" element={<ForgotPassword />} />
                <Route path="/sifre-sifirla/:token" element={<ResetPassword />} />
                <Route path="/dogrula/:token" element={<VerifyEmail />} />
                <Route path="/403" element={<Forbidden />} />
                <Route path="/gizlilik" element={<Privacy />} />
                <Route path="/sartlar" element={<Terms />} />
                <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                  <Route path="/panel" element={<Dashboard />} />
                  <Route path="/odevler" element={<Homework />} />
                  <Route path="/odevler/yeni" element={<RoleGuard allow={TEACHER_ADMIN}><HomeworkNew /></RoleGuard>} />
                  <Route path="/odevler/:id" element={<HomeworkDetail />} />
                  <Route path="/ai/ogretmen" element={<AITeacher />} />
                  <Route path="/ai/kontrol" element={<AIChecker />} />
                  <Route path="/ai/koc" element={<AICoach />} />
                  <Route path="/ai/sinav-uret" element={<RoleGuard allow={TEACHER_ADMIN}><AIExamGen /></RoleGuard>} />
                  <Route path="/sinavlar" element={<Exams />} />
                  <Route path="/sinavlar/:id" element={<ExamDetail />} />
                  <Route path="/yoklama" element={<Attendance />} />
                  <Route path="/mesajlar" element={<Messages />} />
                  <Route path="/basarilar" element={<Gamification />} />
                  <Route path="/bildirimler" element={<Notifications />} />
                  <Route path="/analitik" element={<RoleGuard allow={[...STAFF, "parent"]}><Analytics /></RoleGuard>} />
                  <Route path="/kullanicilar" element={<RoleGuard allow={[...ADMIN, "teacher", "counselor"]}><Users /></RoleGuard>} />
                  <Route path="/kullanicilar/toplu-davet" element={<RoleGuard allow={ADMIN}><BulkInvite /></RoleGuard>} />
                  <Route path="/siniflar" element={<RoleGuard allow={[...ADMIN, "teacher"]}><Classes /></RoleGuard>} />
                  <Route path="/okullar" element={<RoleGuard allow={ADMIN}><Schools /></RoleGuard>} />
                  <Route path="/profil" element={<Profile />} />
                  <Route path="/sertifika" element={<Certificate />} />
                  <Route path="/veli-uyarilari" element={<RoleGuard allow={[...ADMIN, "teacher", "counselor"]}><ParentAlerts /></RoleGuard>} />
                  <Route path="/duyurular" element={<Announcements />} />
                  <Route path="/takvim" element={<CalendarPage />} />
                  <Route path="/odak" element={<FocusTimer />} />
                  <Route path="/defter" element={<Notebook />} />
                  <Route path="/risk-analizi" element={<RoleGuard allow={STAFF}><RiskInsights /></RoleGuard>} />
                  <Route path="/soru-bankasi" element={<RoleGuard allow={TEACHER_ADMIN}><QuestionBank /></RoleGuard>} />
                  <Route path="/raporlar" element={<RoleGuard allow={[...STAFF, "student", "parent"]}><Reports /></RoleGuard>} />
                  <Route path="/2fa" element={<TwoFactor />} />
                  <Route path="/analitik-gelismis" element={<RoleGuard allow={ADMIN}><AdvancedAnalytics /></RoleGuard>} />
                  <Route path="/denetim" element={<RoleGuard allow={["super_admin", "school_admin", "principal"]}><AuditLog /></RoleGuard>} />
                  <Route path="/canli-dersler" element={<LiveLessons />} />
                  <Route path="/gorusmeler" element={<Meetings />} />
                  <Route path="/eksik-konular" element={<RoleGuard allow={STAFF}><TopicGaps /></RoleGuard>} />
                  <Route path="/meydan-okuma" element={<RoleGuard allow={["student"]}><Challenge /></RoleGuard>} />
                </Route>
                <Route element={<ProtectedRoute><RoleGuard allow={["super_admin"]}><PlatformLayout /></RoleGuard></ProtectedRoute>}>
                  <Route path="/platform" element={<Navigate to="/platform/panel" replace />} />
                  <Route path="/platform/panel" element={<PlatformDashboard />} />
                  <Route path="/platform/kiracilar" element={<PlatformTenants />} />
                  <Route path="/platform/satis" element={<PlatformCRM />} />
                  <Route path="/platform/finans" element={<PlatformFinance />} />
                  <Route path="/platform/destek" element={<PlatformSupport />} />
                  <Route path="/platform/surumler" element={<PlatformReleases />} />
                  <Route path="/platform/duyurular" element={<PlatformAnnouncements />} />
                  <Route path="/platform/saglik-uyarilari" element={<PlatformHealthAlerts />} />
                  <Route path="/platform/denetim" element={<PlatformAudit />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </ParentChildProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
